import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  type AIAgent,
  type AIConnector,
  getAgentName,
  getConnectorDisplayName,
  getConnectorId,
} from "@p0security/shared/types/ai-inventory";
import {
  Alert,
  Button,
  Collapse,
  Modal,
  Space,
  Steps,
  Typography,
  message,
} from "antd";
import { CommandDisplay } from "components/Integrations/CommandDisplay";
import copy from "copy-to-clipboard";
import { useCallback, useMemo, useState } from "react";
import { color, font, radius } from "theme/aiInventory";

import {
  slugify,
  useMcpClientRegistration,
} from "../hooks/useMcpClientRegistration.ts";

const { Paragraph, Text } = Typography;

export type SecureConnectorModalProps = {
  open: boolean;
  agent: AIAgent | undefined;
  connectors: AIConnector[];
  onClose: () => void;
};

type PerConnectorConfig = {
  connector: AIConnector;
  label: string;
  snippet: string;
};

type McpClientSnippetMeta = {
  header: string;
};

const MCP_CLIENT_SNIPPETS: Record<string, McpClientSnippetMeta> = {
  "claude-code": {
    header: "# Claude Code MCP server config (~/.config/claude/mcp.json)",
  },
  claude: {
    header: "# Claude Desktop MCP server config (~/.config/claude/mcp.json)",
  },
};

const renderMcpClientSnippet = (
  clientPlatform: string,
  label: string,
  serverUrl: string,
  clientId: string,
  clientSecret: string
): string => {
  const meta = MCP_CLIENT_SNIPPETS[clientPlatform] ?? {
    header: `# MCP server config for ${clientPlatform || "MCP client"} (consult the client's docs for the config file path)`,
  };
  return [
    meta.header,
    '{ "mcpServers": {',
    `    "${slugify(label)}": {`,
    `      "url": "${serverUrl}",`,
    `      "transport": "sse",`,
    `      "auth": { "type": "oauth2",`,
    `                "client_id":     "${clientId}",`,
    `                "client_secret": "${clientSecret}" }`,
    "    }",
    "} }",
  ].join("\n");
};

const renderSnippet = (
  agent: AIAgent,
  connector: AIConnector,
  serverUrl: string,
  clientId: string,
  clientSecret: string
): string => {
  const label = getConnectorDisplayName(connector);
  switch (agent.platform) {
    case "copilot-studio":
      return [
        "# Power Platform custom connector manifest (paste into OpenAPI Definition)",
        'swagger: "2.0"',
        `host: mcp.p0.app`,
        `basePath: /${slugify(label)}/mcp`,
        "schemes: [https]",
        "securityDefinitions:",
        "  p0_oauth:",
        "    type: oauth2",
        "    flow: accessCode",
        `    authorizationUrl: https://mcp.p0.app/oauth/authorize`,
        `    tokenUrl: https://mcp.p0.app/oauth/token`,
        `    # Paste these into Power Platform's OAuth 2.0 connector settings:`,
        `    #   client_id:     ${clientId}`,
        `    #   client_secret: ${clientSecret}`,
        "    scopes: { mcp: 'Invoke P0-brokered MCP tools' }",
      ].join("\n");
    case "local-agents":
      return renderMcpClientSnippet(
        agent.data.clientPlatform,
        label,
        serverUrl,
        clientId,
        clientSecret
      );
  }
};

type CredentialRowProps = {
  label: string;
  secret?: boolean;
  value: string;
};

const CredentialRow: React.FC<CredentialRowProps> = ({
  label,
  value,
  secret = false,
}) => {
  const [revealed, setRevealed] = useState(false);
  const handleCopy = useCallback(() => {
    copy(value);
    void message.success(`${label} copied`);
  }, [label, value]);
  const handleToggle = useCallback(() => setRevealed((v) => !v), []);
  const display =
    secret && !revealed ? "•".repeat(Math.min(value.length, 32)) : value;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        background: color.mute,
        border: `1px solid ${color.rule}`,
        borderRadius: radius.card,
      }}
    >
      <span
        style={{
          minWidth: 110,
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: color.inkMute,
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: 1,
          fontFamily: font.mono,
          fontSize: 12,
          overflowX: "auto",
          whiteSpace: "nowrap",
          color: color.ink,
          padding: "2px 0",
          borderLeft: `1px solid ${color.rule}`,
          paddingLeft: 12,
        }}
      >
        {display}
      </span>
      {secret && (
        <Button
          size="small"
          type="text"
          icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={handleToggle}
        />
      )}
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={handleCopy}
      />
    </div>
  );
};

const STEPS_ITEMS = [
  { title: "Provision credentials" },
  { title: "Copy config" },
  { title: "Verify" },
];

export const SecureConnectorModal: React.FC<SecureConnectorModalProps> = ({
  open,
  agent,
  connectors,
  onClose,
}) => {
  const [step, setStep] = useState(0);
  const registration = useMcpClientRegistration();

  const configs = useMemo<PerConnectorConfig[]>(() => {
    if (!registration.credentials || !agent) return [];
    return connectors.map((connector) => ({
      connector,
      label: getConnectorDisplayName(connector),
      snippet: renderSnippet(
        agent,
        connector,
        registration.credentials!.serverUrl,
        registration.credentials!.clientId,
        registration.credentials!.clientSecret
      ),
    }));
  }, [agent, connectors, registration.credentials]);

  const collapseItems = useMemo(
    () =>
      configs.map((c) => ({
        key: getConnectorId(c.connector),
        label: c.label,
        children: (
          <CommandDisplay commands={c.snippet} minRows={8} maxRows={20} />
        ),
      })),
    [configs]
  );

  const handleProvision = useCallback(async () => {
    if (!agent) return;
    // Register once per agent; the same client credential can be used for all
    // of the agent's connectors.
    await registration.register(getAgentName(agent));
    setStep(1);
  }, [agent, registration]);

  const handleNextStep = useCallback(() => setStep(2), []);

  const handleDone = useCallback(() => {
    onClose();
    // Reset local state so the modal opens fresh next time.
    setStep(0);
  }, [onClose]);

  const footerButtons = useMemo(
    () => [
      <Button key="close" onClick={onClose}>
        Close
      </Button>,
      <Button key="done" type="primary" onClick={handleDone}>
        Done
      </Button>,
    ],
    [handleDone, onClose]
  );

  if (!agent) return null;
  const count = connectors.length;
  const title = `Secure ${count} connector${count === 1 ? "" : "s"} for ${getAgentName(agent)}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={780}
      style={{ maxWidth: "calc(100vw - 48px)" }}
      footer={footerButtons}
      destroyOnClose
    >
      <Steps current={step} size="small" items={STEPS_ITEMS} />
      <div style={{ marginTop: 20 }}>
        {step === 0 && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph>
              P0 will mint a client credential scoped to{" "}
              <Text strong>{getAgentName(agent)}</Text>. Use it to replace the
              agent&apos;s direct {count === 1 ? "connection" : "connections"}{" "}
              with the P0 Gateway.
            </Paragraph>
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              loading={registration.loading}
              onClick={handleProvision}
            >
              Provision client credential
            </Button>
          </Space>
        )}

        {step === 1 && registration.credentials && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                Client credentials
              </Typography.Title>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <CredentialRow
                  label="Server URL"
                  value={registration.credentials.serverUrl}
                />
                <CredentialRow
                  label="Client ID"
                  value={registration.credentials.clientId}
                />
                <CredentialRow
                  label="Client secret"
                  value={registration.credentials.clientSecret}
                  secret
                />
              </Space>
            </div>
            <Paragraph>
              {agent.platform === "copilot-studio"
                ? "Paste the snippet below into the Power Platform connector's OpenAPI definition."
                : "Paste the snippet below into the MCP client's config file."}
            </Paragraph>
            <Collapse
              accordion
              defaultActiveKey={
                configs[0] ? getConnectorId(configs[0].connector) : undefined
              }
              items={collapseItems}
            />
            <Button onClick={handleNextStep}>I have pasted the config</Button>
          </Space>
        )}

        {step === 2 && (
          <Space direction="vertical" size="middle">
            <Paragraph>
              P0 will detect the new configuration on the next assessment run.
              Sessions routed through the secured{" "}
              {count === 1 ? "connector" : "connectors"} will appear in Recent
              activity within a few minutes.
            </Paragraph>
            <Alert
              type="success"
              showIcon
              message="You're done"
              description="Close this dialog. The agent will continue to work while P0 enforces access on its behalf."
            />
          </Space>
        )}
      </div>
    </Modal>
  );
};
